// The exchange's half of the API: the order book, balances, the ramp and the
// command surface. Everything an account has in common with the insurance
// application — sign-in, the PIN, profile, registration — comes from
// @argo/common/api and is re-exported here, so nothing else in this app has to
// know where a call lives.
import { del, get, post, api as common } from '@argo/common/api'

export * from '@argo/common/api'

export const PRICE_SCALE = 1_000_000_000

export type Edge = { parent: string; child: string; kind: string; value?: string }
export type Balance = { symbol: string; amount: string }
export type Order = { chain_key: string; side: 'buy' | 'sell'; give_symbol: string; want_symbol: string; price: string; qty_remaining: string; maker_account?: string }
export type Trade = { maker_account: string; taker_account: string; price: string; qty: string; occurred_at: string; txid: string }
export type Market = { id: number; base: string; quote: string }
export type RampRow = { kind: 'deposit' | 'withdraw'; symbol: string; amount: string; status: string; chain_txid: string | null; created_at: string }
export type LedgerEvent = { block_number: string; tx_id: string; name: string; time: string; account?: string; symbol?: string }
export type Invoke = { tx_id: string; block: number; block_number: number; result: string; activity: unknown }
export type Panel = { panel: string; title: string; rows: [string, unknown][]; text?: string }

export type CommandKind = 'graph' | 'tuna' | 'event'

// The TDB bank bridge. Ids and money columns arrive as strings (exact BIGINT / NUMERIC).
export type BankLink = { id: string; iban: string; holder_name: string | null; status: 'pending' | 'verified'; created_at: string; verified_at: string | null }
export type Payout = {
  id: string; account: string; symbol: string; amount: string; iban: string; holder_name: string | null
  status: string; note: string | null; bank_txn: string | null; requested_at: string; updated_at: string
}
export type BankInfo = {
  account: string; enabled: boolean; symbol: string; bank?: string; iban?: string; name?: string | null
  links: BankLink[]; payouts: Payout[]
}
export type BankDeposit = {
  id: string; refno: string; amount: string; txn_date: string | null; description: string | null
  counterparty_iban: string | null; counterparty_name: string | null; counterparty_bank: string | null
  status: string; account: string | null; chain_txid: string | null; note: string | null
  linked_account: string | null; link_status: string | null; first_name: string | null; last_name: string | null
  seen_at: string
}
export type BankStatus = {
  enabled: boolean; configured: boolean; sandbox: boolean; account: string | null; symbol: string; escrow: string
  bank_name?: string; bank_currency?: string; bank_balance?: number; bank_available?: number; bank_error?: string
  ledger_supply: string; waiting: { kind: string; status: string; n: string }[]
}

/** What the exchange charges, read from the chain. */
export type Fee = { fee_bps: number; fee_account: string }

/**
 * What a side actually receives after the fee.
 *
 * The chaincode truncates, so the rounding goes to the trader — mirrored here
 * rather than rounded the other way, or the form would quote a worse number
 * than the ledger delivers.
 */
export function feeOn(amount: number, bps: number): number {
  if (!bps || bps <= 0 || !Number.isFinite(amount) || amount <= 0) return 0
  return Math.floor((amount * bps) / 10_000)
}

export const api = {
  ...common,
  graph: (target: string) => get<{ target: string; data: Edge[] }>(`/graph?target=${encodeURIComponent(target)}`),
  boot: () => post<{ data: Edge }>('/boot', { child: 'default', kind: 'graph' }),
  command: <T = unknown,>(kind: CommandKind, action: string, target: string, values: Record<string, unknown>, pin?: string) =>
    post<{ data: T }>('/command', { kind, action, target, values, pin }).then(r => r.data),
  balances: (account: string) => get<{ data: Balance[] }>(`/balances?account=${encodeURIComponent(account)}`),
  orders: (account: string) => get<{ data: Order[] }>(`/orders?account=${encodeURIComponent(account)}`),
  book: (market: string) => get<{ data: Order[] }>(`/book?market=${encodeURIComponent(market)}`),
  trades: (market: string) => get<{ data: Trade[] }>(`/trades?market=${encodeURIComponent(market)}`),
  markets: () => get<{ data: Market[] }>('/markets'),
  fee: () => post<{ data: Fee }>('/command', { kind: 'tuna', action: 'getfee', target: 'default', values: {} }).then(r => r.data),
  deposit: (body: { account: string; symbol: string; amount: number; ref?: string; pin?: string }) => post<{ chain_txid: string }>('/deposit', body),
  withdraw: (body: { account: string; symbol: string; amount: number; dest?: string; pin?: string }) => post<{ chain_txid: string }>('/withdraw', body),
  ramp: (account: string) => get<{ data: RampRow[] }>(`/ramp?account=${encodeURIComponent(account)}`),
  events: (limit = 50) => get<{ data: LedgerEvent[] }>(`/events?limit=${limit}`),

  bank: (account: string) => get<BankInfo>(`/bank?account=${encodeURIComponent(account)}`),
  bankLink: (account: string, iban: string) => post<{ link: BankLink }>('/bank/links', { account, iban }),
  bankUnlink: (account: string, id: string) => del<object>(`/bank/links/${id}?account=${encodeURIComponent(account)}`),
  bankPayout: (body: { account: string; iban: string; amount: number; pin?: string }) => post<{ payout: Payout }>('/bank/payouts', body),
  // operator
  bankStatus: () => get<BankStatus>('/bank/status'),
  bankPoll: () => post<{ lines: number; new: number; credited: number }>('/bank/poll', {}),
  bankDeposits: (status?: string) => get<{ data: BankDeposit[] }>(`/bank/deposits${status ? `?status=${status}` : ''}`),
  bankCredit: (id: string, account: string, pin?: string) => post<{ chain_txid: string }>(`/bank/deposits/${id}/credit`, { account, pin }),
  bankRejectDeposit: (id: string, reason: string) => post<object>(`/bank/deposits/${id}/reject`, { reason }),
  bankResolveDeposit: (id: string, txid: string) => post<object>(`/bank/deposits/${id}/resolve`, { txid }),
  bankPayouts: (status?: string) => get<{ data: Payout[] }>(`/bank/payouts${status ? `?status=${status}` : ''}`),
  bankApprove: (id: string, pin?: string) => post<{ payout: Payout }>(`/bank/payouts/${id}/approve`, { pin }),
  bankRejectPayout: (id: string, reason: string) => post<{ payout: Payout }>(`/bank/payouts/${id}/reject`, { reason }),
  bankResolvePayout: (id: string, sent: boolean, bankTxn: string, pin?: string) =>
    post<{ payout: Payout }>(`/bank/payouts/${id}/resolve`, { sent, bank_txn: bankTxn, pin }),

}

/** Order arithmetic shared with the chaincode's fixed-point prices (same as the legacy book.html). */
export function sellOrder(account: string, base: string, quote: string, qty: number, rate: number) {
  return { from: account, to: account, give: base, amount: qty, want: quote, ratio: rate }
}

export function buyOrder(account: string, base: string, quote: string, qty: number, rate: number) {
  const r2z = Math.floor(PRICE_SCALE / rate)
  const quoteAmount = Math.ceil((qty * PRICE_SCALE) / r2z)
  return { from: account, to: account, give: quote, amount: quoteAmount, want: base, ratio: 1 / rate }
}

/** Human rate (quote per base) from a stored order/trade price for the given side. */
export function displayRate(price: string | number, side: 'buy' | 'sell'): number {
  const p = Number(price)
  if (!p) return 0
  return side === 'sell' ? p / PRICE_SCALE : PRICE_SCALE / p
}

/**
 * Base quantity of an order row (buy rows store the quote amount).
 *
 * A sell stores the base amount directly, so it is exact. A buy stores the
 * quote amount against a *truncated* reciprocal price, and `buyOrder` rounds
 * that amount up so the trader can always afford what they asked for. Both
 * roundings are deliberate, but they mean the product below overstates the
 * quantity by up to one price unit: asking to buy 100 AUD at 2600 stored
 * 260001 against a price of 384615 and read back as 100.000284.
 *
 * The true quantity is therefore not a single number but the half-open
 * interval `(raw - step, raw]`. Everything in it placed the same order, so we
 * return the one a person would have typed — the fewest decimal places that
 * still lands inside. That recovers 100 exactly while leaving a genuine
 * partial fill of 12.5 alone.
 */
export function baseQty(row: Order): number {
  const q = Number(row.qty_remaining)
  if (row.side === 'sell') return q
  const price = Number(row.price)
  const raw = (q * price) / PRICE_SCALE
  if (!Number.isFinite(raw) || !price) return raw
  const step = price / PRICE_SCALE
  for (let d = 0; d <= 6; d++) {
    const f = 10 ** d
    const v = Math.round(raw * f) / f
    if (v > raw - step && v <= raw) return v
  }
  return raw
}
